import CloseIcon from "@mui/icons-material/Close";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Box, Dialog, Typography } from "@mui/material";
import styled from "styled-components";

export const StyledCookieIcon = styled(CookieOutlinedIcon)`
  font-size: 18px;
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const StyledSettingsIcon = styled(SettingsOutlinedIcon)`
  font-size: 16px;
`;

export const StyledCloseIcon = styled(CloseIcon)`
  font-size: 20px;
`;

export const BannerOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1400;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: ${({ theme }) => theme.space(50)};
  background: ${({ theme }) => theme.palette.other.backdropOverlay};
  backdrop-filter: blur(4px);
`;

export const BannerContainer = styled(Box)`
  width: 100%;
  max-width: 880px;
  background: ${({ theme }) => theme.palette.background.paper};
  border: 1px solid ${({ theme }) => theme.palette.other.outlineBorder};
  border-radius: ${({ theme }) => theme.radius[30]};
  padding: ${({ theme }) => `${theme.space(50)} ${theme.space(60)}`};
  box-shadow: ${({ theme }) => theme.shadow[30]};
`;

export const BannerHeader = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(30)};
  margin-bottom: ${({ theme }) => theme.space(30)};
`;

export const IconCircle = styled(Box)`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.palette.primary.outlinedHoverBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const BannerDescription = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
  line-height: 1.5;
`;

export const BannerBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(40)};
  margin-top: ${({ theme }) => theme.space(40)};
`;

export const ButtonGroup = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(30)};
  flex-wrap: wrap;
`;

export const AcceptAllButton = styled.button`
  background: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  border: none;
  border-radius: ${({ theme }) => theme.radius[20]};
  padding: ${({ theme }) => `${theme.space(20)} ${theme.space(50)}`};
  font-family: GT-Walsheim, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme.palette.primary.containedHoverBackground};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const RejectAllButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.palette.text.primary};
  border: 1px solid ${({ theme }) => theme.palette.other.outlineBorder};
  border-radius: ${({ theme }) => theme.radius[20]};
  padding: ${({ theme }) => `${theme.space(20)} ${theme.space(50)}`};
  font-family: GT-Walsheim, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.palette.action.hover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ManagePreferencesButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.palette.primary.main};
  border: none;
  padding: ${({ theme }) => `${theme.space(20)} ${theme.space(10)}`};
  font-family: GT-Walsheim, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(10)};

  &:hover {
    text-decoration: underline;
  }
`;

export const LinksRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(20)};
`;

export const PolicyLink = styled.a`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const LinkDivider = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.disabled};
  font-size: 12px;
`;

export const PreferencesDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: ${({ theme }) => theme.palette.background.paper};
    border: 1px solid ${({ theme }) => theme.palette.other.outlineBorder};
    border-radius: ${({ theme }) => theme.radius[30]};
    max-width: 480px;
    width: 100%;
    margin: ${({ theme }) => theme.space(40)};
    overflow: hidden;
  }

  .MuiBackdrop-root {
    background: ${({ theme }) => theme.palette.other.backdropOverlay};
    backdrop-filter: blur(4px);
  }
`;

export const DialogHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.space(40)} ${theme.space(50)}`};
  border-bottom: 1px solid ${({ theme }) => theme.palette.other.outlineBorder};
`;

export const DialogCloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.palette.text.secondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.space(10)};
  border-radius: ${({ theme }) => theme.radius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.palette.action.hover};
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const DialogBody = styled(Box)`
  padding: ${({ theme }) => theme.space(50)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(20)};
`;

export const DialogFooter = styled(Box)`
  padding: ${({ theme }) => `${theme.space(40)} ${theme.space(50)}`};
  border-top: 1px solid ${({ theme }) => theme.palette.other.outlineBorder};
`;

export const CategoryRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space(40)};
  padding: ${({ theme }) => `${theme.space(30)} ${theme.space(40)}`};
  background: ${({ theme }) => theme.palette.interface.default};
  border-radius: ${({ theme }) => theme.radius[20]};
`;

export const CategoryInfo = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(5)};
  flex: 1;
  min-width: 0;
`;

export const CategoryDescription = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 12px;
  line-height: 1.4;
`;

export const AlwaysActiveLabel = styled(Typography)`
  color: ${({ theme }) => theme.palette.success.main};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;

export const SavePreferencesButton = styled.button`
  background: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  border: none;
  border-radius: ${({ theme }) => theme.radius[20]};
  padding: ${({ theme }) => `${theme.space(20)} ${theme.space(50)}`};
  font-family: GT-Walsheim, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  width: 100%;

  &:hover {
    background: ${({ theme }) =>
      theme.palette.primary.containedHoverBackground};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
